import React from 'react';

import { Color } from '@/constants/color';

export interface SpinnerProps {
  /**
   * The color of the spinner icon and label text
   */
  color?: string;

  /**
   * The size of the spinner icon
   */
  size?: number;
  /**
   * Some descriptive text alongside the spinner icon
   */
  label?: string;

  /**
   * Whether to show the text
   */
  visibleLabel?: boolean;

  /**
   * Additional classes to apply to the spinner
   */
  className?: string;
}

interface SpinnerCSSProps {
  bg: string;
  text: string;
  active: string;
}

const ColorVariants: { [key in Color]: SpinnerCSSProps } = {
  [Color.BLACK]: { text: 'text-black-700', bg: 'fill-gray-200', active: 'fill-black-600' },
  [Color.WHITE]: { text: 'text-white-700', bg: 'fill-white', active: 'fill-gray-200' },
  [Color.SLATE]: { text: 'text-slate-700', bg: 'fill-slate-200', active: 'fill-slate-600' },
  [Color.GRAY]: { text: 'text-gray-700', bg: 'fill-gray-200', active: 'fill-gray-600' },
  [Color.ZINC]: { text: 'text-zinc-700', bg: 'fill-zinc-200', active: 'fill-zinc-600' },
  [Color.NEUTRAL]: { text: 'text-neutral-700', bg: 'fill-neutral-200', active: 'fill-neutral-600' },
  [Color.STONE]: { text: 'text-stone-700', bg: 'fill-stone-200', active: 'fill-stone-600' },
  [Color.RED]: { text: 'text-red-700', bg: 'fill-red-200', active: 'fill-red-600' },
  [Color.ORANGE]: { text: 'text-orange-700', bg: 'fill-orange-200', active: 'fill-orange-600' },
  [Color.AMBER]: { text: 'text-amber-700', bg: 'fill-amber-200', active: 'fill-amber-600' },
  [Color.YELLOW]: { text: 'text-yellow-700', bg: 'fill-yellow-200', active: 'fill-yellow-600' },
  [Color.LIME]: { text: 'text-lime-700', bg: 'fill-lime-200', active: 'fill-lime-600' },
  [Color.GREEN]: { text: 'text-green-700', bg: 'fill-green-200', active: 'fill-green-600' },
  [Color.EMERALD]: { text: 'text-emerald-50', bg: 'fill-emerald-200', active: 'fill-emerald-600' },
  [Color.TEAL]: { text: 'text-teal-700', bg: 'fill-teal-200', active: 'fill-teal-600' },
  [Color.CYAN]: { text: 'text-cyan-700', bg: 'fill-cyan-200', active: 'fill-cyan-600' },
  [Color.SKY]: { text: 'text-sky-700', bg: 'fill-sky-200', active: 'fill-sky-600' },
  [Color.BLUE]: { text: 'text-white', bg: 'fill-blue-200', active: 'fill-blue-600' },
  [Color.INDIGO]: { text: 'text-white', bg: 'fill-indigo-200', active: 'fill-indigo-600' }
};

/**
 * @deprecated Do not use this component. There is no equivalent yet at the root
 * of the UI folder — a new version should be built there using the current
 * design-system patterns (tailwind-variants + Headless UI, see src/ui/Button)
 * instead of using this legacy component.
 */
const Spinner: React.FC<SpinnerProps> = ({
  color,
  label = 'Loading...',
  size = 6,
  visibleLabel = true,
  className
}) => {
  const spinnerClasses = `h-${size} w-${size} animate-spin inline-block`;
  const colorStyle = ColorVariants[color as Color] || ColorVariants[Color.SLATE];
  return (
    <span aria-label={label} role="status" className={`inlink-block ${className}`}>
      <svg className={spinnerClasses} viewBox="3 3 18 18">
        <path
          className={colorStyle.bg}
          d="M12 5C8.13401 5 5 8.13401 5 12C5 15.866 8.13401 19 12 19C15.866 19 19 15.866 19 12C19 8.13401 15.866 5 12 5ZM3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12Z"></path>
        <path
          className={colorStyle.active}
          d="M16.9497 7.05015C14.2161 4.31648 9.78392 4.31648 7.05025 7.05015C6.65973 7.44067 6.02656 7.44067 5.63604 7.05015C5.24551 6.65962 5.24551 6.02646 5.63604 5.63593C9.15076 2.12121 14.8492 2.12121 18.364 5.63593C18.7545 6.02646 18.7545 6.65962 18.364 7.05015C17.9734 7.44067 17.3403 7.44067 16.9497 7.05015Z"></path>
      </svg>

      {visibleLabel && <span className={`ml-2 ${colorStyle.text}`}>{label}</span>}
    </span>
  );
};

export default Spinner;
